
import type React from "react"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, ArrowRight, Check, Building, Shield, Clock, Users } from "lucide-react"
import { Button } from "@components/react/pricing/ui/button"

interface GuidedProductSelectorProps {
  isOpen: boolean
  onClose: () => void
  onSelectPlan: (plan: string) => void
}

export function GuidedProductSelector({ isOpen, onClose, onSelectPlan }: GuidedProductSelectorProps) {
  const [step, setStep] = useState(1)
  const [answers, setAnswers] = useState({
    size: "",
    priority: "",
    timeline: "",
  })
  const [recommendedPlan, setRecommendedPlan] = useState("")
  const [explanation, setExplanation] = useState("")

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1)
      setAnswers({
        size: "",
        priority: "",
        timeline: "",
      })
    }
  }, [isOpen])

  // Determine recommended plan based on answers
  useEffect(() => {
    if (step === 4) {
      // Simple recommendation logic
      const { size, priority, timeline } = answers

      // Default to Resilience Essential
      let plan = "Resilience Essential"
      let reason =
        "Based on your needs, we recommend our Resilience Essential plan which provides core data protection capabilities."

      // Recommend Control Plus for larger orgs or those with compliance as priority
      if (size === "500-2000" || size === "2000+" || priority === "compliance") {
        plan = "Control Plus"
        reason =
          "Based on your organization size and priorities, we recommend our Control Plus plan which provides advanced compliance and governance features."
      }

      // Recommend Enterprise for large orgs with immediate needs and comprehensive security
      if (
        (size === "2000+" && timeline === "immediately") ||
        (priority === "comprehensive" && (size === "500-2000" || size === "2000+"))
      ) {
        plan = "Enterprise"
        reason =
          "For your large organization with immediate security needs, our Enterprise plan provides the comprehensive protection and governance you require."
      }

      setRecommendedPlan(plan)
      setExplanation(reason)
    }
  }, [step, answers])

  const handleAnswer = (question: string, answer: string) => {
    setAnswers((prev) => ({
      ...prev,
      [question]: answer,
    }))
    setStep((prev) => prev + 1)
  }

  const handleSelectPlan = () => {
    onSelectPlan(recommendedPlan)
    onClose()
  }

  const handleBack = () => {
    if (step > 1) {
      setStep((prev) => prev - 1)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md mx-4 mb-4 mr-4 bg-white rounded-2xl shadow-2xl overflow-hidden pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-all duration-200"
              aria-label="Close modal"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Progress indicator */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gray-100">
              <motion.div
                className="h-full bg-[#3037e2]"
                initial={{ width: `${(step - 1) * 25}%` }}
                animate={{ width: `${step * 25}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>

            {/* Header */}
            <div className="px-8 pt-8 pb-4">
              <h2 className="text-2xl font-bold text-gray-900">Find your ideal plan</h2>
              <p className="text-sm text-gray-500 mt-1">
                Answer a few quick questions to get a personalized recommendation
              </p>
              <div className="flex items-center mt-4 text-xs text-gray-500">
                <span className="font-medium text-[#3037e2]">Step {step}</span>
                <span className="mx-1">/</span>
                <span>4</span>
              </div>
            </div>

            {/* Content */}
            <div className="px-8 py-6">
              <AnimatePresence mode="wait">
                {step === 1 && (
                  <QuestionStep
                    key="step1"
                    question="How large is your organization?"
                    options={[
                      {
                        id: "under-100",
                        value: "under-100",
                        label: "Under 100 employees",
                        icon: <Users className="h-6 w-6 text-[#3037e2]" />,
                      },
                      {
                        id: "100-500",
                        value: "100-500",
                        label: "100-500 employees",
                        icon: <Users className="h-6 w-6 text-[#3037e2]" />,
                      },
                      {
                        id: "500-2000",
                        value: "500-2000",
                        label: "500-2000 employees",
                        icon: <Building className="h-6 w-6 text-[#3037e2]" />,
                      },
                      {
                        id: "2000+",
                        value: "2000+",
                        label: "2000+ employees",
                        icon: <Building className="h-6 w-6 text-[#3037e2]" />,
                      },
                    ]}
                    onSelect={(value) => handleAnswer("size", value)}
                    selectedValue={answers.size}
                  />
                )}

                {step === 2 && (
                  <QuestionStep
                    key="step2"
                    question="What's your top priority for data security?"
                    options={[
                      {
                        id: "compliance",
                        value: "compliance",
                        label: "Compliance & governance",
                        icon: <Shield className="h-6 w-6 text-[#3037e2]" />,
                      },
                      {
                        id: "protection",
                        value: "protection",
                        label: "Data protection & backup",
                        icon: <Shield className="h-6 w-6 text-[#3037e2]" />,
                      },
                      {
                        id: "comprehensive",
                        value: "comprehensive",
                        label: "Comprehensive security",
                        icon: <Shield className="h-6 w-6 text-[#3037e2]" />,
                      },
                      {
                        id: "cost",
                        value: "cost",
                        label: "Cost optimization",
                        icon: <Shield className="h-6 w-6 text-[#3037e2]" />,
                      },
                    ]}
                    onSelect={(value) => handleAnswer("priority", value)}
                    selectedValue={answers.priority}
                  />
                )}

                {step === 3 && (
                  <QuestionStep
                    key="step3"
                    question="When are you looking to implement a solution?"
                    options={[
                      {
                        id: "immediately",
                        value: "immediately",
                        label: "Immediately",
                        icon: <Clock className="h-6 w-6 text-[#3037e2]" />,
                      },
                      {
                        id: "3-months",
                        value: "3-months",
                        label: "Within 3 months",
                        icon: <Clock className="h-6 w-6 text-[#3037e2]" />,
                      },
                      {
                        id: "future",
                        value: "future",
                        label: "Future planning",
                        icon: <Clock className="h-6 w-6 text-[#3037e2]" />,
                      },
                    ]}
                    onSelect={(value) => handleAnswer("timeline", value)}
                    selectedValue={answers.timeline}
                  />
                )}

                {step === 4 && (
                  <ResultStep
                    key="step4"
                    recommendedPlan={recommendedPlan}
                    explanation={explanation}
                    onSelectPlan={handleSelectPlan}
                  />
                )}
              </AnimatePresence>
            </div>

            {/* Footer */}
            {step < 4 && (
              <div className="px-8 py-4 border-t border-gray-100 flex justify-between">
                {step > 1 ? (
                  <Button variant="secondary" onClick={handleBack}>
                    Back
                  </Button>
                ) : (
                  <div></div> // Empty div to maintain layout
                )}
                <p className="text-xs text-gray-500 self-center">
                  {4 - step} {step === 3 ? "question" : "questions"} remaining
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

interface QuestionStepProps {
  question: string
  options: {
    id: string
    value: string
    label: string
    icon?: React.ReactNode
  }[]
  onSelect: (value: string) => void
  selectedValue: string
}

function QuestionStep({ question, options, onSelect, selectedValue }: QuestionStepProps) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <h3 className="text-xl font-medium text-gray-900">{question}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {options.map((option) => (
          <button
            key={option.id}
            onClick={() => onSelect(option.value)}
            className={`flex items-center p-4 rounded-xl border-2 transition-all duration-200 ${
              selectedValue === option.value
                ? "border-[#3037e2] bg-[#3037e2]/5"
                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            <div className="flex-shrink-0 mr-4">{option.icon}</div>
            <div className="text-left">
              <span className="font-medium">{option.label}</span>
            </div>
            {selectedValue === option.value && (
              <div className="ml-auto">
                <Check className="h-5 w-5 text-[#3037e2]" />
              </div>
            )}
          </button>
        ))}
      </div>
    </motion.div>
  )
}

interface ResultStepProps {
  recommendedPlan: string
  explanation: string
  onSelectPlan: () => void
}

function ResultStep({ recommendedPlan, explanation, onSelectPlan }: ResultStepProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#3037e2]/10 mb-4">
          <Check className="h-8 w-8 text-[#3037e2]" />
        </div>
        <h3 className="text-xl font-medium text-gray-900">Your recommended plan</h3>
      </div>

      <div className="bg-gradient-to-r from-[#3037e2]/5 to-[#3037e2]/10 rounded-xl p-6 text-center">
        <h4 className="text-2xl font-bold text-[#3037e2]">{recommendedPlan}</h4>
        <p className="mt-2 text-gray-600">{explanation}</p>
      </div>

      <div className="space-y-3">
        <Button
          onClick={onSelectPlan}
          className="w-full bg-[#3037e2] hover:bg-[#2731c8] h-12 rounded-full flex items-center justify-center gap-2"
        >
          Get started with {recommendedPlan}
          <ArrowRight className="h-4 w-4" />
        </Button>
        <Button variant="secondary" className="w-full" onClick={onSelectPlan}>
          Compare all plans
        </Button>
      </div>
    </motion.div>
  )
}
